
import { Logger } from 'sdk/logger';
import { Store } from '@reduxjs/toolkit';

import {
    ActionTypes,
    sliceName,
    TESTING_ENDPOINTS_RGX
} from './globalConfigConfig';
import { BaseUrl, ConfigOptions } from './globalConfigInterface';
import * as selectors from './globalConfigSelector';
import BaseModule from 'sdk/base/base-module';
import globalSpinner from 'sdk/connector/globalSpinner';

/**
 * Manages global configurations for an SDK framework. This class implements a singleton pattern.
 */
export default class GlobalConfigApi extends BaseModule {
    public static instance: GlobalConfigApi;
    private options?: ConfigOptions;
    /**
     * Creates an instance of GlobalConfigApi. If an instance already exists, it returns that instance.
     * @param {Store} store - The Redux store used to maintain application state.
     */
    constructor(store: Store) {
        if (GlobalConfigApi.instance) {
            return GlobalConfigApi.instance;
        }
        super(store, ActionTypes, sliceName);
        this.setGlobalSpinnerVisibility = this.setGlobalSpinnerVisibility.bind(this);
        this.isGlobalSpinnerVisible = this.isGlobalSpinnerVisible.bind(this);
        this._initBaseURL();
       
        GlobalConfigApi.instance = this;
    }

    getOptions = () => {
        if (!this.options) {
            this.options = {
                auth: {
                    in: 'HEADER',
                    key: 'Authorization'
                }
            };
        }
        return this.options;
    };


    /**
     * Returns the singleton instance of the GlobalConfigApi class.
     * @returns {GlobalConfigApi}
     */
    static getInstance() {
        return GlobalConfigApi.instance; 
    }

    config = (options: ConfigOptions) => {
        this.options = options;
    };

    /**
     * Retrieves the base URL from the Redux store using a selector.
     * @returns {string}
     */
    getBaseURL = () => {
        return selectors.baseUrlSelector(this.store.getState());
    };
    /**
     * Retrieves the auth info from the Redux store using a selector.
     * @returns {string}
     */
    getAuthInfo = () => {
        return selectors.authInfoSelector(this.store.getState());
    };

    /**
     * Updates the session authentication token by dispatching an action and returns a success response.
     * @param {string} auth - The new authentication token.
     * @returns {Object}
     */
    updateSessionAuth = (auth: string) => {
        this.dispatchAction(ActionTypes.UPDATE_SESSION_AUTH, auth);
        return this.sdkResponser.success();
    };
    /**
     * Updates the session Tenant and Server Ids by dispatching an action and returns a success response.
     * @param {string} tenantId - The Tenant Id
     * @returns {SDKResponser} Success / Failure
     */
    updateTenantAndServerId = (tenantId?: string, serverId?: string) => {
        this.dispatchAction(ActionTypes.UPDATE_TENANT_ID, { tenantId, serverId });
        return this.sdkResponser.success();
    };

    /**
     * Update the Flow Session Id in the Redux store to be sent back to the BE PP
     * @param sessionId The Flow Session Id (Generated in BE)
     * @returns {SDKResponser} Success / Failure
     */
    updateSessionId = (sessionId: string) => {
        this.dispatchAction(ActionTypes.UPDATE_SESSION_ID, sessionId);
        return this.sdkResponser.success();
    };
    
    /**
     * Retrieves the session authentication token from the Redux store using a selector.
     * @returns {string}
     */
    getSessionAuth = () => {
        return selectors.sessionAuthSelector(this.store.getState());
    };

    /**
     * Sets the visibility of the global spinner.
     * @param {boolean} [flag=true] - The desired visibility of the spinner.
     */
    setGlobalSpinnerVisibility = (flag?: boolean) => {
        this.dispatchAction(ActionTypes.SET_SPINNER_VISIBILITY, !!flag);
    };

    /**
     * Checks if the global spinner is visible.
     * @returns {boolean}
     */
    isGlobalSpinnerVisible = () => {
        return !!selectors.isSpinnerVisible(this.store.getState());
    };

    /**
     * Updates the base URL and handles storage and dispatch.
     * @param {string} newBaseUrl - The new base URL to be set.
     * @returns {Object}
     */
    updateBaseUrl = (newBaseUrl: string) => {
        if (!newBaseUrl) {
            return this.sdkResponser.error('BASE_URL_FAILURE', `Failed to dispatch ${newBaseUrl}`);
        }
        const builtUrl = this._buildURL(newBaseUrl);
        if (!builtUrl) {
            return this.sdkResponser.error('BASE_URL_FAILURE', `Failed to build ${newBaseUrl}`);
        }
        if (window) {
            window.sessionStorage.setItem('baseUrl', newBaseUrl);
        }
        if (!this.dispatchAction(ActionTypes.UPDATE_BASE_URL, builtUrl)) {
            this.sdkResponser.error('BASE_URL_FAILURE', `Failed to dispatch ${newBaseUrl}`);
        }
        return this.sdkResponser.success();
    };

    /**
     * Hides the global spinner by clearing it and dispatching an action to set its visibility to false.
     */
    hideSpinner = () => {
        globalSpinner.clear();
        this.dispatchAction(ActionTypes.SET_SPINNER_VISIBILITY, false);
    };

    /**
     * Initializes the base URL from session storage and updates it using `updateBaseUrl`.
     */
    _initBaseURL() {
        const baseURL = window?.sessionStorage?.getItem?.('baseUrl');
        this.updateBaseUrl(baseURL || '');
    }

    /**
     * Constructs a full URL from a given string. Validates and constructs a `BaseUrl` object if successful.
     * @param {string} newUrl - The URL string to build.
     * @returns {BaseUrl | null}
     */
    _buildURL(newUrl: string): BaseUrl | null {
        let url;
        try {
            url = new URL(!newUrl.startsWith('http') ? `http://${newUrl}` : newUrl);
        } catch (err) {
            Logger.warn(sliceName, `Failed to build URL: ${newUrl}...`);
            Logger.log(sliceName, err, 'error');
            return null;
        }
        return {
            host: url.host,
            port: url.port,
            protocol: url.protocol,
            url: `${url.origin}`,
            isTestingEndpoint: TESTING_ENDPOINTS_RGX.test(newUrl)
        };
    }

}
